import { Avatar, Button, Carousel, Checkbox, Label } from 'flowbite-react';
import React, { Fragment, useCallback, useMemo } from 'react';
import { BsPatchCheckFill } from 'react-icons/bs';
import Stories from 'react-insta-stories';
import { useNavigate, useParams } from 'react-router-dom';
import { generatePositionTitle, stringAvatar } from 'shared-utils';
import AnimatedProgressBar from 'src/components/shared/AnimatedProgressBar';
import { useGetLevelDistributionByIdQuery, useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import { useMorph, useMorphKeys } from 'react-morph';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { easeInSin, easeOutSin } from 'react-morph/lib/easings';
import { IoMdPerson } from 'react-icons/io';
import LevelHistogram from 'src/components/position/preview/LevelHistogram';
import MobileBottomButton from 'src/components/position/preview/MobileBottomButton';
import useBottomCheck from 'src/hooks/utils/useBottomCheck';
import { TbInfoCircleFilled } from 'react-icons/tb';
import { useMutation } from 'react-query';
import { completeLevelExplainer, confirmLevelFn } from 'src/apis/position';
import { useToast } from 'src/context/ToastProvider';
import CreatingPositionLoader from 'src/components/position/CreatingPositionLoader';
import BottomButton from 'src/components/position/preview/BottomButton';

import CountUp from 'react-countup';
import { Odometer } from 'odometer_countup';
import { Fade, Slide, useMediaQuery } from '@mui/material';
import _ from 'lodash';

const Explainers = () => {
	const toast = useToast();
	const { jobId } = useParams();
	const navigate = useNavigate();
	const [currentIndex, setCurrentIndex] = React.useState(0);
	const { data: positionData } = useGetPositionByIdQuery(jobId);
	const [selectedJobLevel, setSelectedJobLevel] = React.useState<number>(0);
	const [activeIndex, setActiveIndex] = React.useState<number>(0);
	const isAtBottom = useBottomCheck();
	const { data: levelData, isLoading } = useGetLevelDistributionByIdQuery(jobId, d => {
		setSelectedJobLevel(d.recommendedLevel || 0);
		setActiveIndex(
			Math.max(
				0,
				d.salaryDistributionData?.findIndex(sd =>
					_.inRange(d.recommendedLevel || 0, sd.range.min || 0, sd.range.max || 0),
				) || 0,
			),
		);
	});
	const [showLevelExplainer, setShowLevelExplainer] = React.useState(
		positionData?.position?.showLevelExplainer || false,
	);

	const isMobile = useMediaQuery('(max-width:960px)');

	const morph2: any = useMorphKeys(['container', 'containerTwo', 'image', 'title', 'subtitle'], {
		isReversed: showLevelExplainer,
	});

	const morph = useMorph({
		isReversed: !showLevelExplainer,
		easings: {
			translateX: easeOutSin,
			translateY: easeInSin,
		},
	});

	const [isBoxChecked, setIsBoxChecked] = React.useState<boolean>(false);
	const levelConfirmFn = useMutation(['confirmLevel'], confirmLevelFn, {
		onSuccess() {
			navigate(`/positions/${jobId}/candidates`);
		},
		onError(err: any) {
			toast({
				title: err.response.data.message,
				type: 'error',
			});
		},
	});

	const completeLevelExplainerFn = useMutation(['completeLevelExplainer'], completeLevelExplainer, {
		onError(err: any) {
			toast({
				title: err.response.data.message,
				type: 'error',
			});
		},
	});

	React.useEffect(() => {
		if (positionData?.position?.isPublished) {
			navigate('/');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [positionData?.position?.company?.logo, positionData?.position?.company?.url]);

	const stories: any = [
		{
			content: (props: { showBg: boolean }) => (
				<div className="bg-white w-full h-full relative isolate">
					<div
						style={{
							backgroundImage:
								props?.showBg || isMobile
									? 'url(/assets/explainer-bg.png), linear-gradient(180deg, #C4E2FF 0%, #F0F8FF 100%)'
									: 'linear-gradient(180deg, #C4E2FF 0%, #F0F8FF 100%)',
						}}
						className="absolute inset-0 h-full w-full z-[-1] bg-cover bg-no-repeat bg-[50%_50%] bg-blend-multiply"
					></div>
					<div className="flex flex-col w-full h-full max-w-[420px] mx-auto overflow-auto" style={{ padding: 20 }}>
						<div className="mx-auto my-auto">
							<img src="/assets/levelupExplainerIcon.svg" alt="logo" />
						</div>
						<div className="my-auto">
							<h1 className="text-[#1F3343] font-inter text-2xl text-center font-[700] leading-[30px]">
								Deciphering Candidate
								<br /> Level <img className="inline-flex" src="/assets/levelupGradientIcon.svg" /> on LevelUp
							</h1>
							<p className="mt-[20px] text-center text-[#555770] font-inter text-lg font-[500]">
								A combination of diverse skills, <br />
								pre-assessed by Industry Experts.
							</p>
						</div>
					</div>
				</div>
			),
		},
		{
			content: (props: { showBg: boolean }) => (
				<div className="bg-white w-full h-full relative isolate">
					<div
						style={{
							backgroundImage:
								props?.showBg || isMobile
									? 'url(/assets/explainer-bg.png), linear-gradient(180deg, #C4E2FF 0%, #F0F8FF 100%)'
									: 'linear-gradient(180deg, #C4E2FF 0%, #F0F8FF 100%)',
						}}
						className="absolute inset-0 h-full w-full z-[-1] bg-cover bg-no-repeat bg-[50%_50%] bg-blend-multiply"
					></div>
					<div
						className="max-w-[420px] mx-auto flex flex-col w-full h-full relative isolate overflow-auto"
						style={{ padding: 20 }}
					>
						<div className="mx-auto my-auto">
							<img src="/assets/explanainer-users.png" alt="logo" className="max-h-[380px]" />
						</div>
						<div className="my-auto">
							<h1 className="text-[#1F3343] font-inter text-2xl text-center font-[700] leading-[30px]">
								Discover 100+ Skills of Pre-assessed Candidates Before the Interview
							</h1>
							<p className="mt-[20px] text-center text-[#555770] font-inter text-lg font-[500]">
								Higher Candidate Level Indicate Greater Skills and Salary Expectations
							</p>
						</div>
					</div>
				</div>
			),
		},
		{
			content: (props: { showBg: boolean; showData: boolean }) => {
				return (
					<div className="bg-white w-full h-full relative isolate">
						<div
							style={{
								backgroundImage:
									props?.showBg || isMobile
										? 'url(/assets/explainer-bg.png), linear-gradient(180deg, #C4E2FF 0%, #F0F8FF 100%)'
										: 'linear-gradient(180deg, #C4E2FF 0%, #F0F8FF 100%)',
							}}
							className="absolute inset-0 h-full w-full z-[-1] bg-cover bg-no-repeat bg-[50%_50%] bg-blend-multiply"
						></div>
						<div
							className="overflow-auto max-w-[420px] mx-auto flex flex-col w-full h-full relative isolate"
							style={{ padding: 20 }}
						>
							<div className="mt-auto">
								<p className="text-[#1F3343] font-inter text-lg font-[600]">
									Your ideal candidate must possess the following essential skills to crack your interview process:
								</p>

								<div className="mt-[10px] px-4 py-[10px] rounded-[12px] bg-white border border-[#9DBFF9] w-full">
									<div className="flex items-center gap-2">
										<Avatar
											className="w-[48px] h-[48px] min-w-[48px] min-h-[48px] [&_div]:!w-full [&_div]:!h-full object-cover [&_img]:w-full [&_img]:h-full [&_img]:!ring-[1px] [&_img]:!ring-[#7777E7] [&_img]:object-cover border border-[#F2F2F5] rounded-full"
											img={'/assets/tech-rockstar.png'}
											placeholderInitials={'TR'}
											rounded
										/>

										<div>
											<div className="flex items-center gap-1">
												<h1 className="text-lg font-[600] text-[#1C1C28]">Tech Rockstar</h1>
												<BsPatchCheckFill className="text-[#7777E7]" />
											</div>
											<div className="flex flex-wrap gap-1 items-center sm:text-[12px] text-sm font-[500] text-[#28293D] dark:text-[#E4E4EB]">
												<div>
													{generatePositionTitle(positionData?.position?.title || '')}
													{positionData?.position?.company ? ' @ ' + positionData?.position?.company.name : ''}
												</div>
												<div className="font-inter font-[500] text-sm text-[#28293D] dark:text-[#C7C9D9]">•</div>
												<div>
													Exp
													{` ${Math.floor(positionData?.position?.positionRequirement?.workExperience?.min || 0)} Yrs ${Math.ceil(positionData?.position?.positionRequirement?.workExperience?.min || 0 * 12) % 12
														} Mon - ${Math.floor(
															positionData?.position?.positionRequirement.workExperience.max || 0,
														)} Yrs ${Math.ceil(positionData?.position?.positionRequirement?.workExperience?.max || 0 * 12) % 12
														} Mon `}
												</div>
											</div>
										</div>
									</div>

									<div className="mt-[9px] space-y-[10px]">
										{positionData?.position?.positionRequirement.skillThresholds
											?.sort((a, b) => b.threshold - a.threshold)
											.slice(0, 3)
											?.map((skillThreshold, i) => (
												<AnimatedProgressBar
													key={i}
													pathBgColor="white"
													showMaxValue={false}
													label={<>{skillThreshold?.displaySkill}</>}
													value={skillThreshold?.threshold}
													delay={i * 500}
												/>
											))}
									</div>

									<div className="bg-[#E3FFF1] pt-1 mx-[-16px] mb-[-10px] rounded-b-[12px] px-4 pb-[10px] mt-[19px]">
										<div className="flex justify-between items-center mb-[5px] text-base text-[#1F2A37] font-[500]">
											<p>Overall Level</p>

											<span className="text-[#1F2A37] font-inter text-xl text-center font-[700] leading-[30px]">
												<img className="inline-flex" src="/assets/levelupGradientIcon.svg" />
												<CountUp
													end={Math.round(positionData?.position?.positionRequirement?.levelThreshold || 0)}
													plugin={new Odometer()}
												/>
											</span>
										</div>

										<AnimatedProgressBar
											showMaxValue={false}
											pathBgColor="#E3FFF1"
											showLabel={false}
											delay={200}
											label={<></>}
											value={Math.round(positionData?.position?.positionRequirement?.levelThreshold || 0)}
										/>

										<div className="mt-2">
											<p className="text-[12px] text-[#1C1C28] italic font-[500] font-inter">
												Only candidates at or above the specified levels can apply.
											</p>
										</div>
									</div>
								</div>
							</div>

							<Fade
								in={props?.showData || (currentIndex === 2 && showLevelExplainer)}
								style={{
									transitionDelay: props?.showData || (currentIndex === 2 && showLevelExplainer) ? '500ms' : '0ms',
									transitionDuration: '1000ms',
								}}
							>
								<div className="my-auto">
									<h1 className="text-[#1F3343] font-inter text-base text-center font-[500] leading-[24px]">
										Number of candidates available on LevelUp matching the above criteria{' '}
										<span className="text-2xl font-[700] ml-2 text-[#05A660]">
											<CountUp end={levelData?.recommendations || 0} plugin={new Odometer()} />
										</span>
									</h1>

									<div>
										{positionData?.recommendations && positionData?.recommendations?.length > 0 ? (
											<Avatar.Group className="justify-center -space-x-2 mt-4">
												{positionData?.recommendations
													.sort((a, b) => {
														// Users with a profile image come first
														if (a.user.profile.profileImageUrl || a.user.profile.imageUrl) return -1;
														if (b.user.profile.profileImageUrl || b.user.profile.imageUrl) return 1;
														return 0;
													})
													.slice(0, 5)
													.map((recommendation, i) => (
														<Avatar
															key={i}
															className={`w-[50px] h-[50px] [&_div]:!w-full [&_div]:!h-full [&_img]:w-full [&_img]:h-full ${recommendation.user.profile.profileImageUrl || recommendation.user.profile.imageUrl
																? '[&_img]:!ring-[1px] [&_img]:!ring-[#7777E7] [&_img]:object-cover'
																: ''
																} [&>div>div]:!ring-[1px] [&>div>div]:!ring-[#7777E7]`}
															img={
																recommendation?.user?.profile?.profileImageUrl ||
																recommendation?.user?.profile?.imageUrl
															}
															placeholderInitials={stringAvatar(recommendation?.user?.name.trim().toUpperCase() || '')}
															rounded
															stacked
														/>
													))}
												{positionData?.recommendations?.length > 5 && (
													<div className="font-inter font-[500] p-[10px] text-sm leading-[14px] w-[50px] h-[50px] rounded-full bg-[#CDF] ring-[#7777E7] ring-[1px] text-[#1F3343] z-[1] flex flex-wrap justify-center items-center text-center">
														<span className="text-xl leading-[20px] font-[600] ">
															+
															{levelData && levelData.recommendations && levelData.recommendations > 5
																? levelData.recommendations - 5 || 0
																: 0 || positionData?.recommendations?.length - 5}
														</span>
														<span>more</span>
													</div>
												)}
											</Avatar.Group>
										) : (
											<div className="text-center">No recommendations to display</div>
										)}
									</div>
								</div>
							</Fade>

							{/* Dummy button for spacing */}
							<Button
								color="purplePlaced"
								className="invisible relative z-[1000] w-full max-w-[240px] mx-auto bg-[#7777E7] text-base font-medium font-inter mt-auto"
							>
								Got it!
							</Button>
							{/* Dummy button for spacing */}
						</div>
					</div>
				);
			},
		},
	];

	const Slider = useMemo(
		() => (
			<Carousel
				leftControl={' '}
				rightControl={' '}
				theme={{
					indicators: {
						active: {
							off: 'bg-[#D6D6D6] hover:bg-[#597ecf] dark:bg-gray-800/50 dark:hover:bg-gray-800',
							on: 'bg-[#7777E7] dark:bg-gray-800',
						},
					},
				}}
			>
				{stories.map((story: any, id: number) => (
					<Fragment key={id}>{React.createElement(story.content, { showBg: true, showData: true })}</Fragment>
				))}
			</Carousel>
		),
		[positionData, levelData],
	);

	if (levelConfirmFn.isLoading)
		return (
			<CreatingPositionLoader
				startingCustomText="Matching candidates for the job posting..."
				midTimeCustomText="Matching candidates for the job posting..."
				almostDoneCustomText="Almost there..."
			/>
		);

	return (
		<div className="relative">
			{showLevelExplainer && (
				<div key="one" {...morph2.container}>
					<Stories
						stories={stories}
						defaultInterval={5000}
						width={'100%'}
						height={'100dvh'}
						storyStyles={{
							width: 'auto',
							margin: 'auto',
							maxWidth: '100%',
							maxHeight: '100%',
						}}
						progressStyles={{ background: '#0063F7', maxWidth: isMobile ? '100%' : '115px' }}
						progressWrapperStyles={{ background: 'white', maxWidth: isMobile ? '100%' : '115px' }}
						progressContainerStyles={{
							height: '64px',
							width: '100%',
							paddingInline: '4px',
							background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%)',
						}}
						currentIndex={currentIndex}
						onPrevious={() => {
							if (currentIndex === 0) {
								navigate(`/job-preview/${jobId}`);
							}
						}}
						onStoryStart={(data: number) => {
							setCurrentIndex(data);
						}}
						onAllStoriesEnd={() => {
							// if (positionData?.position?.showLevelExplainer) {
							// 	completeLevelExplainerFn.mutate({ positionId: jobId || '' });
							// }
							// setShowLevelExplainer(false);
						}}
					/>

					<Slide
						direction="up"
						in={currentIndex === 2 && showLevelExplainer}
						mountOnEnter
						unmountOnExit
						timeout={{
							enter: 600,
						}}
					>
						<Button
							color="purplePlaced"
							className="absolute bottom-[5%] inset-x-0 !z-[1000] w-full max-w-[240px] mx-auto bg-[#7777E7] text-base font-medium font-inter mt-auto"
							onClick={e => {
								e.stopPropagation();
								if (positionData?.position?.showLevelExplainer) {
									completeLevelExplainerFn.mutate({ positionId: jobId || '' });
								}
								setShowLevelExplainer(false);
							}}
						>
							Got it!
						</Button>
					</Slide>
				</div>
			)}

			{isMobile && !showLevelExplainer && (
				<div className="px-[16px] py-[20px] pb-[70px]">
					<div key="two" className="mt-auto">
						<p className="text-[#1C1C28] font-inter text-sm font-[500]" {...morph2.container}>
							Your ideal candidate must possess the following essential skills to crack your interview process:
						</p>

						<div
							{...morph2.container}
							className="mt-[10px] px-4 py-[10px] rounded-[12px] bg-white border border-[#9DBFF9] w-full"
						>
							<div className="flex items-center gap-2">
								<Avatar
									className="w-[48px] h-[48px] [&_div]:!w-full [&_div]:!h-full object-cover [&_img]:w-full [&_img]:h-full [&_img]:!ring-[1px] [&_img]:!ring-[#7777E7] [&_img]:object-cover border border-[#F2F2F5] rounded-full"
									img={'/assets/tech-rockstar.png'}
									placeholderInitials={'TR'}
									rounded
								/>

								<div>
									<div className="flex items-center gap-1">
										<h1 className="text-lg font-[600] text-[#1C1C28]">Tech Rockstar</h1>
										<BsPatchCheckFill className="text-[#7777E7]" />
									</div>
									<div className="flex flex-wrap gap-1 items-center sm:text-[12px] text-sm font-[500] text-[#28293D] dark:text-[#E4E4EB]">
										<div>
											{generatePositionTitle(positionData?.position?.title || '')}
											{positionData?.position?.company ? ' @ ' + positionData?.position?.company.name : ''}
										</div>
										<div className="font-inter font-[500] text-sm text-[#28293D] dark:text-[#C7C9D9]">•</div>
										<div>
											Exp
											{` ${Math.floor(positionData?.position?.positionRequirement?.workExperience?.min || 0)} Yrs ${Math.ceil(positionData?.position?.positionRequirement?.workExperience?.min || 0 * 12) % 12
												} Mon - ${Math.floor(positionData?.position?.positionRequirement.workExperience.max || 0)} Yrs ${Math.ceil(positionData?.position?.positionRequirement?.workExperience?.max || 0 * 12) % 12
												} Mon `}
										</div>
									</div>
								</div>
							</div>

							<div className="mt-[9px] space-y-[10px]">
								{positionData?.position?.positionRequirement.skillThresholds
									?.sort((a, b) => b.threshold - a.threshold)
									.slice(0, 3)
									?.map((skillThreshold, i) => (
										<AnimatedProgressBar
											key={i}
											pathBgColor="white"
											showMaxValue={false}
											label={<>{skillThreshold?.displaySkill}</>}
											value={skillThreshold?.threshold}
											delay={i * 500}
										/>
									))}
							</div>

							<div className="bg-[#E3FFF1] pt-1 mx-[-16px] mb-[-10px] rounded-b-[12px] px-4 pb-[10px] mt-[19px]">
								<div className="flex justify-between items-center mb-[5px] text-base text-[#1F2A37] font-[500]">
									<p>Overall Level</p>

									<span className="text-[#1F2A37] font-inter text-xl text-center font-[700] leading-[30px]">
										<img className="inline-flex" src="/assets/levelupGradientIcon.svg" />
										<CountUp
											end={Math.round(positionData?.position?.positionRequirement?.levelThreshold || 0)}
											plugin={new Odometer()}
										/>
									</span>
								</div>

								<AnimatedProgressBar
									showMaxValue={false}
									pathBgColor="#E3FFF1"
									showLabel={false}
									delay={200}
									label={<></>}
									value={Math.round(positionData?.position?.positionRequirement?.levelThreshold || 0)}
								/>

								<div className="mt-2">
									<p className="text-[12px] text-[#1C1C28] italic font-[500] font-inter">
										Only candidates at or above the specified levels can apply.
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="h-[1px] w-full border-t-[1px] border-[#9CA3AF] border-dashed my-[20px]"></div>

					<div>
						<p {...morph2.subtitle}>Lastly, you are hiring at</p>

						<div
							className="mt-[10px] relative p-4 rounded-[12px] bg-white border border-[#9DBFF9] w-full"
							{...morph2.containerTwo}
						>
							<button
								className="absolute right-[10px] top-[10px]"
								onClick={() => {
									setCurrentIndex(0);
									setShowLevelExplainer(true);
								}}
							>
								<TbInfoCircleFilled className="text-[#FFCC00] w-[24px] h-[24px]" />
							</button>
							<div className="flex items-center justify-between max-w-[80%]">
								<div className="flex items-center gap-2">
									<p className="text-base font-inter font-[500] text-[#555770]">Candidate Level</p>
									<img className="inline-flex" src="/assets/levelupGradientIcon.svg" />
								</div>

								<p className="text-2xl font-inter font-[700] text-[#1C1C28]">
									<CountUp
										end={Math.round(
											selectedJobLevel || positionData?.position?.positionRequirement?.levelThreshold || 0,
										)}
									// plugin={new Odometer()}
									/>
								</p>
							</div>

							<div className="flex items-center justify-between max-w-[80%] mt-[12px]">
								<div className="flex items-center gap-2">
									<p className="text-base font-inter font-[500] text-[#555770]">Matching candidates</p>
									<IoMdPerson className="text-[#7777E7] w-[20px] h-[20px]" />
								</div>

								<p className="text-2xl font-inter font-[700] text-[#1C1C28]">
									<CountUp
										end={
											Math.round(selectedJobLevel) === Math.round(levelData?.recommendedLevel || 0)
												? levelData?.recommendations || 0
												: levelData?.salaryDistributionData?.length
													? levelData?.salaryDistributionData[activeIndex].users || 0
													: 0
										}
									// plugin={new Odometer()}
									/>
								</p>
							</div>

							<p className="text-center mt-[36px] text-[#555770] text-xs font-inter font-[400]">
								(We{' '}
								<span className="text-[#00B7C4]">
									recommend Level {Math.round(Number(levelData?.recommendedLevel) / 10) * 10}
								</span>{' '}
								to match with the top applications for your requirement)
							</p>
						</div>
					</div>

					<div className="mt-[22px] min-h-[150ox]">
						<LevelHistogram
							isLoading={isLoading}
							position={positionData?.position}
							activeIndex={activeIndex}
							setActiveIndex={setActiveIndex}
							levelData={levelData}
							jobId={jobId || ''}
							selectedJobLevel={selectedJobLevel}
							setSelectedJobLevel={setSelectedJobLevel}
						/>
					</div>

					<div className="flex gap-2 mt-[30px]" id="positionAgreement">
						<Checkbox
							color="purplePlaced"
							id="agree"
							className="mt-[3px]"
							onChange={e => setIsBoxChecked(e.target.checked)}
						/>
						<Label htmlFor="agree" className="text-[#1C1C28] text-xs font-inter font-[400]">
							I understand and agree: Once published, job details can't be edited.
						</Label>
					</div>

					<div
						className="fixed bottom-0 w-full mx-[-16px] md:hidden"
						style={{
							transition: 'background-color 0.2s ease-in-out',
							backgroundColor: isAtBottom ? 'transparent' : 'white',
						}}
					>
						<MobileBottomButton
							onBack={() => {
								navigate(`/job-preview/${jobId}`);
							}}
							isLoading={levelConfirmFn.isLoading}
							onNext={() => {
								if (isBoxChecked) {
									// If LMS Level TechStack is enabled, send lmsTechStack instead of level
									if (positionData?.position?.autoRecommendationEnable && positionData?.position?.lmsTechStack && Array.isArray(positionData.position.lmsTechStack) && positionData.position.lmsTechStack.length > 0) {
										levelConfirmFn.mutate({
											positionId: jobId || '',
											lmsTechStack: positionData.position.lmsTechStack.map((ts: any) => {
												// Ensure techStack is always a string (ObjectId)
												// Handle case where techStack might be an object
												let techStackId: string;
												const techStackValue = ts.techStack || ts;
												if (typeof techStackValue === 'string') {
													techStackId = techStackValue;
												} else if (techStackValue && typeof techStackValue === 'object') {
													// If it's an object, try to get _id or toString()
													const techStackObj = techStackValue as any;
													if (techStackObj._id) {
														techStackId = String(techStackObj._id);
													} else if (typeof techStackObj.toString === 'function') {
														techStackId = techStackObj.toString();
													} else {
														techStackId = String(techStackValue);
													}
												} else {
													techStackId = String(techStackValue);
												}
												return {
													techStack: techStackId,
													priority: ts.priority ?? 0,
												};
											}),
										});
									} else {
										levelConfirmFn.mutate({ level: selectedJobLevel, positionId: jobId || '' });
									}
								} else {
									document.getElementById('positionAgreement')?.scrollIntoView();
								}
							}}
							disableNextButton={levelConfirmFn.isLoading || (isAtBottom && !isBoxChecked)}
							nextBtnText={<>Continue</>}
						/>
					</div>
				</div>
			)}

			{!isMobile && !showLevelExplainer && (
				<div className="flex items-center h-[100dvh] justify-center gap-[20px] px-4">
					<div
						{...morph}
						className="max-h-[750px] h-full overflow-auto flex-1 block max-w-[760px] w-full flex-col gap-5 py-5 px-2 md:px-6 my-[20px] rounded-[12px] bg-[#FAFAFA] dark:bg-[#111E2C] border "
					>
						<div className="max-w-max">
							<p>Lastly, you are hiring at</p>

							<div className="mt-[10px] relative p-4 rounded-[12px] bg-white border border-[#9DBFF9] w-full">
								<button
									className="absolute right-[10px] top-[10px]"
									onClick={() => {
										setCurrentIndex(0);
										setShowLevelExplainer(true);
									}}
								>
									<TbInfoCircleFilled className="text-[#FFCC00] w-[24px] h-[24px]" />
								</button>
								<div className="flex items-center justify-between max-w-[80%]">
									<div className="flex items-center gap-2">
										<p className="text-base font-inter font-[500] text-[#555770]">Candidate Level</p>
										<img className="inline-flex" src="/assets/levelupGradientIcon.svg" />
									</div>

									<p className="text-2xl font-inter font-[700] text-[#1C1C28]">
										<CountUp
											end={Math.round(
												selectedJobLevel || positionData?.position?.positionRequirement?.levelThreshold || 0,
											)}
										// plugin={new Odometer()}
										/>
									</p>
								</div>

								<div className="flex items-center justify-between max-w-[80%] mt-[12px]">
									<div className="flex items-center gap-2">
										<p className="text-base font-inter font-[500] text-[#555770]">Matching candidates</p>
										<IoMdPerson className="text-[#7777E7] w-[20px] h-[20px]" />
									</div>

									<p className="text-2xl font-inter font-[700] text-[#1C1C28]">
										<CountUp
											end={
												Math.round(selectedJobLevel) === Math.round(levelData?.recommendedLevel || 0)
													? levelData?.recommendations || 0
													: levelData?.salaryDistributionData?.length
														? levelData?.salaryDistributionData[activeIndex].users || 0
														: 0
											}
										// plugin={new Odometer()}
										/>
									</p>
								</div>

								<p className="text-center mt-[36px] text-[#555770] text-xs font-inter font-[400]">
									(We{' '}
									<span className="text-[#00B7C4]">
										recommend Level {Math.round(Number(levelData?.recommendedLevel) / 10) * 10}
									</span>{' '}
									to match with the top applications for your requirement)
								</p>
							</div>
						</div>

						<div className="mt-[30px]">
							<LevelHistogram
								isLoading={isLoading}
								position={positionData?.position}
								activeIndex={activeIndex}
								setActiveIndex={setActiveIndex}
								levelData={levelData}
								jobId={jobId || ''}
								selectedJobLevel={selectedJobLevel}
								setSelectedJobLevel={setSelectedJobLevel}
							/>
						</div>

						<BottomButton
							selectedJobLevel={selectedJobLevel}
							jobId={jobId || ''}
							position={positionData?.position}
							onClick={() => {
								// If auto recommendation is enabled, send lmsTechStack and subRole instead of level
								if (positionData?.position?.autoRecommendationEnable && positionData?.position?.lmsTechStack && Array.isArray(positionData.position.lmsTechStack) && positionData.position.lmsTechStack.length > 0) {
									levelConfirmFn.mutate({
										positionId: jobId || '',
										lmsTechStack: positionData.position.lmsTechStack.map((ts: any) => {
											// Ensure techStack is always a string (ObjectId)
											// Handle case where techStack might be an object
											let techStackId: string;
											const techStackValue = ts.techStack || ts;
											if (typeof techStackValue === 'string') {
												techStackId = techStackValue;
											} else if (techStackValue && typeof techStackValue === 'object') {
												// If it's an object, try to get _id or toString()
												const techStackObj = techStackValue as any;
												if (techStackObj._id) {
													techStackId = String(techStackObj._id);
												} else if (typeof techStackObj.toString === 'function') {
													techStackId = techStackObj.toString();
												} else {
													techStackId = String(techStackValue);
												}
											} else {
												techStackId = String(techStackValue);
											}
											return {
												techStack: techStackId,
												priority: ts.priority ?? 0,
											};
										}),
										subRole: positionData.position.subRole && Array.isArray(positionData.position.subRole) && positionData.position.subRole.length > 0 ? positionData.position.subRole : undefined,
									});
								} else {
									levelConfirmFn.mutate({ level: selectedJobLevel, positionId: jobId || '' });
								}
							}}
						/>
					</div>

					<div className="flex-1 max-w-[407px] h-full max-h-[750px]">{Slider}</div>
				</div>
			)}
		</div>
	);
};

export default Explainers;
